import React from 'react';
import { useAsistenciaDocente } from 'hooks/useAsistenciaDocente';
import { AsignacionItem } from 'types/matricula.types';
import {
  EstadoAsistencia,
  ESTADO_LABEL,
  ESTADO_COLOR,
  ESTADO_DOT,
  ESTADOS,
} from 'types/asistencia.types';

// ============================================================
// AsistenciaDocentePage – Registro y gestión de asistencias
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const CARD_ACCENTS = [
  'border-t-blue-500', 'border-t-purple-500', 'border-t-green-500',
  'border-t-amber-500', 'border-t-pink-500', 'border-t-indigo-500',
];

// ── Selector de cursos ───────────────────────────────────────
const CursoSelector: React.FC<{
  asignaciones: AsignacionItem[];
  onSelect: (a: AsignacionItem) => void;
}> = ({ asignaciones, onSelect }) => {
  const porAnio = asignaciones.reduce<Record<number, AsignacionItem[]>>((acc, a) => {
    if (!acc[a.anio]) acc[a.anio] = [];
    acc[a.anio].push(a);
    return acc;
  }, {});
  const anios = Object.keys(porAnio).map(Number).sort((a, b) => b - a);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Asistencia</h1>
        <p className="text-slate-400 text-sm mt-0.5">Selecciona un curso para registrar asistencia</p>
      </div>
      {anios.map(anio => (
        <div key={anio} className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Año {anio}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {porAnio[anio].map((a, idx) => (
              <button
                key={a.idAsignacion}
                onClick={() => onSelect(a)}
                className={`text-left bg-white rounded-2xl border border-slate-100 shadow-sm
                  border-t-4 ${CARD_ACCENTS[idx % CARD_ACCENTS.length]}
                  transition-all hover:shadow-md hover:-translate-y-0.5 p-5`}
              >
                <h3 className="font-bold text-slate-800 text-base mb-2">{a.cursoNombre}</h3>
                <p className="text-sm text-slate-600 mb-1.5">
                  {a.gradoNombre} — Sección <strong>{a.seccionNombre}</strong>
                </p>
                <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full
                  ${NIVEL_COLOR[a.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                  {a.gradoNivel}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Botones de estado ────────────────────────────────────────
const EstadoBtn: React.FC<{
  estado: EstadoAsistencia;
  activo: boolean;
  onClick: () => void;
}> = ({ estado, activo, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all
      ${activo
        ? `${ESTADO_COLOR[estado]} shadow-sm`
        : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
      }`}
  >
    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activo ? ESTADO_DOT[estado] : 'bg-slate-300'}`} />
    {ESTADO_LABEL[estado]}
  </button>
);

// ── Componente principal ─────────────────────────────────────
const AsistenciaDocentePage: React.FC = () => {
  const {
    asignaciones, loadingAsig, selectedAsignacion, seleccionarAsignacion, volver,
    fecha, setFecha,
    estudiantes, loadingData, error, yaRegistrado,
    localMap, setEstado, setMotivo,
    submitting, submitError, submitSuccess, guardar,
  } = useAsistenciaDocente();

  if (loadingAsig) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  if (asignaciones.length === 0) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-bold text-slate-800">Asistencia</h1>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center">
          <p className="text-slate-400 text-sm font-medium">No tienes cursos asignados</p>
        </div>
      </div>
    );
  }

  if (!selectedAsignacion) {
    return <CursoSelector asignaciones={asignaciones} onSelect={seleccionarAsignacion} />;
  }

  // Contadores en tiempo real del mapa local
  const conteo = ESTADOS.reduce<Record<EstadoAsistencia, number>>((acc, e) => {
    acc[e] = Object.values(localMap).filter(v => v.estado === e).length;
    return acc;
  }, {} as Record<EstadoAsistencia, number>);

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={volver}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
          </svg>
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-800">{selectedAsignacion.cursoNombre}</h1>
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full
              ${NIVEL_COLOR[selectedAsignacion.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
              {selectedAsignacion.gradoNivel}
            </span>
          </div>
          <p className="text-slate-400 text-sm">
            {selectedAsignacion.gradoNombre} — Sección {selectedAsignacion.seccionNombre} — {selectedAsignacion.anio}
          </p>
        </div>
      </div>

      {/* Selector de fecha + resumen */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between
                      bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-slate-600">Fecha:</label>
          <input
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
          {yaRegistrado && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
              Ya registrada
            </span>
          )}
        </div>

        {/* Contadores */}
        {estudiantes.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {ESTADOS.map(e => (
              <span key={e} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${ESTADO_COLOR[e]}`}>
                <span className={`w-2 h-2 rounded-full ${ESTADO_DOT[e]}`} />
                {ESTADO_LABEL[e]}: {conteo[e]}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabla */}
      {loadingData ? (
        <div className="flex justify-center py-12"><SpinnerIcon /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>
      ) : estudiantes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
          <p className="text-slate-400 text-sm">No hay estudiantes matriculados</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide w-8">#</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Estudiante</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {estudiantes.map((est, idx) => {
                const local = localMap[est.idMatricula] ?? { estado: 'PRESENTE' as EstadoAsistencia, motivo: '' };
                const necesitaMotivo = local.estado === 'AUSENTE' || local.estado === 'TARDANZA' || local.estado === 'JUSTIFICADO';

                return (
                  <tr key={est.idMatricula} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-slate-400">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">
                        {est.estudianteApellidos}, {est.estudianteNombres}
                      </p>
                      <p className="text-xs text-slate-400">{est.estudianteDni}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {ESTADOS.map(e => (
                          <EstadoBtn
                            key={e}
                            estado={e}
                            activo={local.estado === e}
                            onClick={() => setEstado(est.idMatricula, e)}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {necesitaMotivo ? (
                        <input
                          type="text"
                          value={local.motivo}
                          onChange={ev => setMotivo(est.idMatricula, ev.target.value)}
                          placeholder="Motivo (opcional)"
                          className="w-full max-w-xs px-3 py-1.5 rounded-lg border border-slate-200 text-sm
                                     text-slate-800 bg-slate-50 placeholder-slate-300
                                     focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer con guardar */}
      {estudiantes.length > 0 && !loadingData && (
        <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4">
          <div>
            {submitSuccess && (
              <span className="text-green-600 text-sm font-semibold flex items-center gap-1.5">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                </svg>
                Asistencia guardada correctamente
              </span>
            )}
            {submitError && (
              <span className="text-red-600 text-sm">{submitError}</span>
            )}
          </div>
          <button
            onClick={guardar}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold
                       bg-blue-700 hover:bg-blue-800 text-white transition-colors shadow-sm disabled:opacity-60"
          >
            {submitting ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
                <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
              </svg>
            )}
            {yaRegistrado ? 'Actualizar Asistencia' : 'Guardar Asistencia'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AsistenciaDocentePage;
